import PropTypes from 'prop-types';
import { AutocompleteInput, maxLength, minLength, ReferenceInput, required, SelectInput, TextInput, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContractorLogoInput from '@/components/contractors/ContractorLogoInput.jsx';
import { BrandLogoField } from '@/components/brand/fields/BrandLogoField.jsx';
import { createFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { retailChainSectionStyles } from '@/themes/commonStyles/retailChainForm.js';
import { isAllowedLogoFile, LOGO_MAX_BYTES } from '@/components/retailChain/validators/retailChainValidators.js';

const StyledSection = styled(Box)(retailChainSectionStyles);
const retailChainFilterToQuery = createFilterToQuery(['name']);

export const BrandBasicInfo = ({ showRetailChain = true, lockedRetailChain = null }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const hasExistingLogo = Boolean(record?.currentLogo || record?.logo);

    const validateLogoFormat = file => {
        const files = Array.isArray(file) ? file : [file];
        if (files.every(item => !item || isAllowedLogoFile(item))) {
            return undefined;
        }

        return translate('resources.brand.validation.logoFormat');
    };

    let retailChainField = null;
    if (lockedRetailChain) {
        retailChainField = (
            <SelectInput
                source='lockedRetailChainId'
                label={translate('resources.brand.fields.retailChain')}
                choices={[{ id: lockedRetailChain.id, name: lockedRetailChain.name }]}
                defaultValue={lockedRetailChain.id}
                disabled
                fullWidth
            />
        );
    } else if (showRetailChain) {
        retailChainField = (
            <Box className='autocomplete-parent'>
                <ReferenceInput source='retailChainId' reference='retail-chain'>
                    <AutocompleteInput
                        filterToQuery={retailChainFilterToQuery}
                        validate={required(translate('common.validation.required'))}
                        label={translate('resources.brand.fields.retailChain')}
                        fullWidth
                        disablePortal
                    />
                </ReferenceInput>
            </Box>
        );
    }

    return (
        <StyledSection>
            <Typography variant='h5'>{translate('resources.brand.labels.basicInfo')}</Typography>
            <TextInput
                source='name'
                label={translate('resources.brand.fields.name')}
                validate={[
                    required(translate('common.validation.required')),
                    minLength(3, translate('common.validation.minLength', { count: 3 })),
                    maxLength(200, translate('common.validation.maxLength', { count: 200 })),
                ]}
                fullWidth
            />
            {retailChainField}
            <ContractorLogoInput
                source='logo'
                record={record}
                maxBytes={LOGO_MAX_BYTES}
                maxSizeMessage={translate('resources.brand.validation.logoTooLarge')}
                instructionText={translate('resources.brand.validation.logoFormatsHint')}
                validate={validateLogoFormat}
                existingPreview={hasExistingLogo ? <BrandLogoField width={160} height={100} /> : null}
            />
            <TextInput
                source='description'
                label={translate('resources.brand.fields.description')}
                validate={[maxLength(200, translate('common.validation.maxLength', { count: 200 }))]}
                fullWidth
            />
        </StyledSection>
    );
};

BrandBasicInfo.propTypes = {
    showRetailChain: PropTypes.bool,
    lockedRetailChain: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
    }),
};
